"use client";

import { c as _c } from "react/compiler-runtime";
import { IconChevronRightOutline18 } from "nucleo-ui-outline-18";
import { useFilteredTokens } from "@/hooks/useFilteredTokens";
import type { SimBalanceItem } from "@/hooks/useSimTokenInfo";
import { PixelLoader } from "@/components/ui/pixel-loader";
import { useMemo, useState } from "react";
import { WalletTokenBalanceRow, tokenKey } from "./WalletTokenBalanceRow";
interface TokenBalanceListProps {
  balances: SimBalanceItem[];
  isLoading: boolean;
  chainInfoMap: Map<number, any>;
  onTransferClick: (balance: SimBalanceItem, humanBalance: string) => void;
  showTransferButton?: boolean;
  onSwapClick?: (balance: SimBalanceItem, humanBalance: string) => void;
  showSwapButton?: boolean;
  swapDisabled?: boolean;
  swapDisabledReason?: string;
  selectable?: boolean;
  selectedKeys?: Set<string>;
  onSelectChange?: (balance: SimBalanceItem, selected: boolean) => void;
  transferDisabled?: boolean;
  transferDisabledReason?: string;
}
const DUST_THRESHOLD_USD = 0.001;

/**
 * Determine if a token has a valid price quote.
 * A token has a quote if price_usd is a positive number.
 */
function hasQuote(balance: SimBalanceItem): boolean {
  return typeof balance.price_usd === "number" && balance.price_usd > 0;
}

/**
 * Split tokens into three tiers:
 * - visible: has quote AND value > dust threshold
 * - dust: has quote but value <= dust threshold
 * - spam: no quote at all (therefore no value)
 */
function categorizeTokens(balances: SimBalanceItem[]) {
  const visible: SimBalanceItem[] = [];
  const dust: SimBalanceItem[] = [];
  const spam: SimBalanceItem[] = [];
  for (const balance of balances) {
    if (!hasQuote(balance)) {
      spam.push(balance);
    } else if ((balance.value_usd || 0) <= DUST_THRESHOLD_USD) {
      dust.push(balance);
    } else {
      visible.push(balance);
    }
  }
  return {
    visible,
    dust,
    spam
  };
}
function computeHumanBalance(balance: SimBalanceItem): string {
  const balanceBigInt = BigInt(balance.amount);
  const divisor = BigInt(10 ** balance.decimals);
  const wholePart = balanceBigInt / divisor;
  const remainder = balanceBigInt % divisor;
  const decimalPart = remainder.toString().padStart(balance.decimals, "0").replace(/0+$/, "");
  return decimalPart ? `${wholePart}.${decimalPart}` : wholePart.toString();
}

// ─── Collapsible section for dust / spam ────────────────────────────────────

interface CollapsedTokenSectionProps {
  label: string;
  count: number;
  tokens: SimBalanceItem[];
  chainInfoMap: Map<number, any>;
  onTransferClick: (balance: SimBalanceItem, humanBalance: string) => void;
  showTransferButton: boolean;
  onSwapClick?: (balance: SimBalanceItem, humanBalance: string) => void;
  showSwapButton?: boolean;
  swapDisabled?: boolean;
  swapDisabledReason?: string;
  selectable?: boolean;
  selectedKeys?: Set<string>;
  onSelectChange?: (balance: SimBalanceItem, selected: boolean) => void;
  transferDisabled?: boolean;
  transferDisabledReason?: string;
  keyPrefix: string;
}
export function CollapsedTokenSection(t0) {
  const $ = _c(25);
  const {
    label,
    count,
    tokens,
    chainInfoMap,
    onTransferClick,
    showTransferButton,
    onSwapClick,
    showSwapButton,
    swapDisabled,
    swapDisabledReason,
    selectable,
    selectedKeys,
    onSelectChange,
    transferDisabled,
    transferDisabledReason,
    keyPrefix
  } = t0;
  const [expanded, setExpanded] = useState(false);
  if (count === 0) {
    return null;
  }
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => setExpanded(_temp);
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const t2 = `h-3.5 w-3.5 transition-transform duration-150 ${expanded ? "rotate-90" : ""}`;
  let t3;
  if ($[1] !== t2) {
    t3 = <IconChevronRightOutline18 className={t2} />;
    $[1] = t2;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const t4 = expanded ? `Hide ${count} ${label}` : `Show ${count} ${label}`;
  let t5;
  if ($[3] !== t3 || $[4] !== t4) {
    t5 = <div className="px-4 py-2.5"><button type="button" onClick={t1} className="text-ds-text-disabled hover:text-ds-text-subtle dark:text-ds-text-muted text-2-5xs flex items-center gap-1.5 font-medium transition-colors dark:hover:text-zinc-300">{t3}{t4}</button></div>;
    $[3] = t3;
    $[4] = t4;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] !== chainInfoMap || $[7] !== expanded || $[8] !== keyPrefix || $[9] !== onSelectChange || $[10] !== onSwapClick || $[11] !== onTransferClick || $[12] !== selectable || $[13] !== selectedKeys || $[14] !== showSwapButton || $[15] !== showTransferButton || $[16] !== swapDisabled || $[17] !== swapDisabledReason || $[18] !== tokens || $[19] !== transferDisabled || $[20] !== transferDisabledReason) {
    t6 = expanded && tokens.map((balance, idx) => {
      const chainInfo = chainInfoMap.get(balance.chain_id);
      const humanBalance = computeHumanBalance(balance);
      return <WalletTokenBalanceRow key={`${keyPrefix}${balance.chain_id}-${balance.address}-${idx}`} balance={balance} chainInfo={chainInfo} humanBalance={humanBalance} showTransferButton={showTransferButton} onTransferClick={onTransferClick} showSwapButton={showSwapButton} onSwapClick={onSwapClick} swapDisabled={swapDisabled} swapDisabledReason={swapDisabledReason} selectable={selectable} isSelected={selectedKeys?.has(tokenKey(balance))} onSelectChange={onSelectChange} transferDisabled={transferDisabled} transferDisabledReason={transferDisabledReason} />;
    });
    $[6] = chainInfoMap;
    $[7] = expanded;
    $[8] = keyPrefix;
    $[9] = onSelectChange;
    $[10] = onSwapClick;
    $[11] = onTransferClick;
    $[12] = selectable;
    $[13] = selectedKeys;
    $[14] = showSwapButton;
    $[15] = showTransferButton;
    $[16] = swapDisabled;
    $[17] = swapDisabledReason;
    $[18] = tokens;
    $[19] = transferDisabled;
    $[20] = transferDisabledReason;
    $[21] = t6;
  } else {
    t6 = $[21];
  }
  let t7;
  if ($[22] !== t5 || $[23] !== t6) {
    t7 = <>{t5}{t6}</>;
    $[22] = t5;
    $[23] = t6;
    $[24] = t7;
  } else {
    t7 = $[24];
  }
  return t7;
}

// ─── Main list ──────────────────────────────────────────────────────────────
function _temp(prev) {
  return !prev;
}
export function TokenBalanceList({
  balances,
  isLoading,
  chainInfoMap,
  onTransferClick,
  showTransferButton = true,
  onSwapClick,
  showSwapButton = false,
  swapDisabled,
  swapDisabledReason,
  selectable = false,
  selectedKeys,
  onSelectChange,
  transferDisabled,
  transferDisabledReason
}: TokenBalanceListProps) {
  const {
    filtered: serviceFiltered,
    spam: serviceSpam,
    isFiltered
  } = useFilteredTokens(balances);

  // Start from service-filtered tokens (removes known scams), then categorize
  const baseTokens = isFiltered ? serviceFiltered : balances;
  const extraSpam = isFiltered ? serviceSpam : [];
  const {
    visible,
    dust,
    spam
  } = useMemo(() => {
    const result = categorizeTokens(baseTokens);
    // Merge service-detected spam into our spam bucket
    result.spam = [...result.spam, ...extraSpam];
    return result;
  }, [baseTokens, extraSpam]);
  if (isLoading) {
    return <div className="body-sm flex items-center gap-2">
        <PixelLoader size="sm" />
        Loading balances...
      </div>;
  }
  if (!balances || balances.length === 0) {
    return <div className="text-ds-text-muted mt-4 py-4 text-sm">No tokens found</div>;
  }
  return <div className="divide-ds-neutral-200 dark:divide-ds-neutral-700 divide-y">
      {visible.map((balance, idx) => {
      const chainInfo = chainInfoMap.get(balance.chain_id);
      const humanBalance = computeHumanBalance(balance);
      return <WalletTokenBalanceRow key={`${balance.chain_id}-${balance.address}-${idx}`} balance={balance} chainInfo={chainInfo} humanBalance={humanBalance} showTransferButton={showTransferButton} onTransferClick={onTransferClick} showSwapButton={showSwapButton} onSwapClick={onSwapClick} swapDisabled={swapDisabled} swapDisabledReason={swapDisabledReason} selectable={selectable} isSelected={selectedKeys?.has(tokenKey(balance))} onSelectChange={onSelectChange} transferDisabled={transferDisabled} transferDisabledReason={transferDisabledReason} />;
    })}
      <CollapsedTokenSection label="dust tokens" count={dust.length} tokens={dust} chainInfoMap={chainInfoMap} onTransferClick={onTransferClick} showTransferButton={showTransferButton} onSwapClick={onSwapClick} showSwapButton={showSwapButton} swapDisabled={swapDisabled} swapDisabledReason={swapDisabledReason} selectable={selectable} selectedKeys={selectedKeys} onSelectChange={onSelectChange} transferDisabled={transferDisabled} transferDisabledReason={transferDisabledReason} keyPrefix="dust-" />
      <CollapsedTokenSection label="spam tokens" count={spam.length} tokens={spam} chainInfoMap={chainInfoMap} onTransferClick={onTransferClick} showTransferButton={showTransferButton} onSwapClick={onSwapClick} showSwapButton={showSwapButton} swapDisabled={swapDisabled} swapDisabledReason={swapDisabledReason} selectable={selectable} selectedKeys={selectedKeys} onSelectChange={onSelectChange} transferDisabled={transferDisabled} transferDisabledReason={transferDisabledReason} keyPrefix="spam-" />
    </div>;
}